import React, { useContext, useState } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Page,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import {
  getTranslatedText,
  SaveCard,
  toast,
  useActivityTracker
} from '@nile/components';
import { AccountClient, useCachedQuery } from '@nile/graphql';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import {
  GET_REPORT_CONFIGURATION,
  UPDATE_REPORT
} from '@nile/reports/src/modules/reports/graphql';
import { pick } from 'lodash';
import { useMutation } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { ContentWrapper, SuffixWrapper } from './style';

function ReportConfiguration(props) {
  const { callBack, data: reportData, fromModal } = props;

  const reportsContext = useContext(ReportsContext);
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(reportData);

  const { loading, data: metaData } = useCachedQuery(GET_REPORT_CONFIGURATION, {
    client: AccountClient,
    skip: !reportData?.reportId,
    variables: {
      reportId: reportData?.reportId
    }
  });

  const [updateReport] = useMutation(UPDATE_REPORT, {
    client: AccountClient
  });

  const handleInputChange = (key, obj, value) => {
    if (key === 'enable') {
      const newMetrics = data?.reportConfig?.metrics?.map((met) =>
        met?.metric === obj?.metric ? { ...met, enabled: value } : met
      );
      setData((prevState) => ({
        ...prevState,
        reportConfig: { metrics: newMetrics }
      }));
    }
    if (key === 'unit' || key === 'data') {
      const newMetrics = data?.reportConfig?.metrics?.map((met) =>
        met?.metric === obj?.metric
          ? {
              ...met,
              condition: {
                ...met?.condition,
                value: { ...met?.condition?.value, [key]: value }
              }
            }
          : met
      );
      setData((prevState) => ({
        ...prevState,
        reportConfig: { metrics: newMetrics }
      }));
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    const updatedReportData = pick(data, [
      'reportId',
      'name',
      'description',
      'reportConfig'
    ]);
    updateReport({
      variables: {
        reportId: reportData?.reportId,
        input: updatedReportData
      }
    })
      .then((res) => {
        reportsContext?.updateReportDataContext(res?.data?.updateReport);
        useActivityTracker({
          event: 'Report configuration changed'
        });
        toast('success', 'reports.dashboard.updateDashboard.success');
      })
      .finally(() => {
        setIsLoading(false);
        if (callBack) {
          callBack();
        }
      });
  };

  const getSuffixType = (type) => {
    const suffixType = type?.input?.thresholdUnit?.type;
    const suffixData = data?.reportConfig?.metrics?.find(
      (met) => met?.metric === type?.metric
    );
    switch (suffixType) {
      case 'select':
        return (
          <SuffixWrapper adaptHeight>
            <Dropdown
              isDisabled={
                !data?.reportConfig?.metrics?.find(
                  (met) => met?.metric === type?.metric
                )?.enabled
              }
              options={type?.input?.thresholdUnit?.choices}
              value={type?.input?.thresholdUnit?.choices?.find(
                (chs) => chs?.value === suffixData?.condition?.value?.unit
              )}
              onChange={(value) =>
                handleInputChange('unit', type, value?.value)
              }
            />
          </SuffixWrapper>
        );
      case 'staticText':
        return (
          <SuffixWrapper adaptWidth>
            {suffixData?.condition?.value?.unit}
          </SuffixWrapper>
        );
      default:
        return <></>;
    }
  };

  return (
    <Page
      variant="secondary"
      divider
      contentHeight="100px"
      title={
        <div className="fx actr">
          {fromModal && (
            <div className="mr-16">
              <Button
                onClick={callBack}
                icon={<SVGIcon name="BackArrow" size="sm" />}
              />
            </div>
          )}
          {getTranslatedText(intl, 'reports.report.asset.health.config.title')}
        </div>
      }
      primaryAction={
        <SaveCard
          removeSecondaryBtnBg
          handleSave={handleSave}
          actions={{ back: callBack }}
          loading={isLoading}
        />
      }
    >
      <ContentWrapper className="p2040">
        {metaData?.getReportConfigMeta?.metrics?.map((el) => (
          <>
            <div className="fx actr g10 mt-16">
              {el?.uiType === 'checkbox' && (
                <Checkbox
                  label={
                    <TextStyle
                      variation={
                        data?.reportConfig?.metrics?.find(
                          (met) => met?.metric === el?.metric
                        )?.enabled
                          ? 'null'
                          : 'subdued'
                      }
                    >
                      <FormattedMessage id={el?.label} />
                    </TextStyle>
                  }
                  checked={
                    data?.reportConfig?.metrics?.find(
                      (met) => met?.metric === el?.metric
                    )?.enabled
                  }
                  onChange={(value) => handleInputChange('enable', el, value)}
                />
              )}

              {el?.input && (
                <div className="fx actr smallTextField">
                  <TextField
                    type={el?.input?.thresholdValue?.type}
                    size="extrasmall"
                    disabled={
                      !data?.reportConfig?.metrics?.find(
                        (met) => met?.metric === el?.metric
                      )?.enabled
                    }
                    value={
                      data?.reportConfig?.metrics?.find(
                        (met) => met?.metric === el?.metric
                      )?.condition?.value?.data
                    }
                    onChange={(value) => handleInputChange('data', el, value)}
                  />
                  {getSuffixType(el)}
                </div>
              )}
              {el?.info && (
                <TextStyle>
                  <FormattedMessage id={el?.info} />
                </TextStyle>
              )}
            </div>
          </>
        ))}
      </ContentWrapper>
    </Page>
  );
}

export default ReportConfiguration;
